import { useEffect } from "react";
import { MessageCircle, Phone, Check, CheckCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadFormWidget from "@/components/LeadFormWidget";
import Reviews from "@/components/Reviews";
import Gallery from "@/components/Gallery";
import ServiceAreas from "@/components/ServiceAreas";
import FinalCTA from "@/components/FinalCTA";
import { getServiceLandingBySlug, SITE_URL } from "@/data/serviceLandingData";
import { trackLead, toE164 } from "@/lib/tracking";
import { openWhatsApp as openWhatsAppHelper } from "@/utils/phone";

const DEFAULT_TITLE = "BEKAJ BAU | Renovierung, Innenausbau & Sanierung aus Großkrotzenburg";
const DEFAULT_DESCRIPTION = "BEKAJ BAU aus Großkrotzenburg bietet Innenausbau, Renovierungen, Sanierungen, Fassadenarbeiten und Bodenbeschichtungen im Raum Hanau, Aschaffenburg und Umgebung.";
const JSONLD_ID = "bekaj-service-page-jsonld";
const CANONICAL_ID = "bekaj-canonical";

const AREAS = [
  "Hanau", "Aschaffenburg", "Frankfurt am Main", "Offenbach", "Großkrotzenburg",
  "Hainburg", "Seligenstadt", "Rodgau", "Maintal", "Alzenau", "Main-Kinzig-Kreis",
];

// SEO pro Route: document.title, Meta-Description, Canonical, Service-JSON-LD (idempotent)
const useServiceSeo = (data) => {
  useEffect(() => {
    if (!data) return;
    window.scrollTo(0, 0);

    document.title = data.metaTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", data.metaDescription);

    let canonical = document.getElementById(CANONICAL_ID);
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      canonical.id = CANONICAL_ID;
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}${data.canonicalPath}`;

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_URL}${data.canonicalPath}#service`,
          name: data.formService,
          serviceType: data.formService,
          description: data.metaDescription,
          url: `${SITE_URL}${data.canonicalPath}`,
          provider: { "@id": `${SITE_URL}/#business` },
          areaServed: AREAS.map((name) => ({ "@type": "City", name })),
        },
        {
          "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
          "@id": `${SITE_URL}/#business`,
          name: "BEKAJ BAU",
          telephone: "+4917620283482",
          url: `${SITE_URL}/`,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Mainstr. 6",
            postalCode: "63538",
            addressLocality: "Großkrotzenburg",
            addressCountry: "DE",
          },
          areaServed: AREAS.map((name) => ({ "@type": "City", name })),
        },
      ],
    };
    let jsonLd = document.getElementById(JSONLD_ID);
    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.type = "application/ld+json";
      jsonLd.id = JSONLD_ID;
      document.head.appendChild(jsonLd);
    }
    jsonLd.text = JSON.stringify(schema);

    return () => {
      document.title = DEFAULT_TITLE;
      const md = document.querySelector('meta[name="description"]');
      if (md) md.setAttribute("content", DEFAULT_DESCRIPTION);
      document.getElementById(CANONICAL_ID)?.remove();
      document.getElementById(JSONLD_ID)?.remove();
    };
  }, [data]);
};

const ServiceHero = ({ data, settings }) => {
  const whatsappText = `Hallo, ich interessiere mich für ${data.formService}.`;
  const telHref = `tel:${toE164(settings?.phone) || "+4917620283482"}`;

  const handleWhatsApp = () => {
    trackLead("whatsapp", data.slug);
    openWhatsAppHelper(settings?.whatsapp_number || "4917620283482", whatsappText);
  };

  return (
    <section
      className="relative pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-br from-[#faf8f5] via-white to-[#f5f0ea]"
      data-testid="service-hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="pt-2">
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4A331D] leading-tight mb-4"
              style={{ fontFamily: "Work Sans, sans-serif" }}
              data-testid="service-hero-headline"
            >
              {data.h1}
            </h1>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4" data-testid="service-hero-subline">
              {data.heroSubline}
            </p>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6" data-testid="service-hero-intro">
              {data.intro}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="service-trust-bar">
              {data.vorteile.map((text, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700 font-medium">{text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Button
                onClick={() => {
                  document.getElementById("landing-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="bg-[#D4AA6A] hover:bg-[#c49a5a] text-[#4A331D] px-6 py-5 text-base font-bold rounded-lg shadow-md"
                data-testid="service-hero-offer-button"
              >
                <FileText className="w-5 h-5 mr-2" />
                Kostenloses Angebot
              </Button>
              <Button
                onClick={handleWhatsApp}
                className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-5 text-base font-semibold rounded-lg shadow-md"
                data-testid="service-hero-whatsapp-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
              <Button
                asChild
                className="bg-[#4A331D] hover:bg-[#593B23] text-white px-6 py-5 text-base font-semibold rounded-lg shadow-md"
                data-testid="service-hero-call-button"
              >
                <a href={telHref} onClick={() => trackLead("anruf", data.slug)} data-track="phone_click" data-track-value="service_hero">
                  <Phone className="w-5 h-5 mr-2" />
                  Jetzt anrufen
                </a>
              </Button>
            </div>
          </div>

          <div className="w-full scroll-mt-24" id="landing-form">
            <div className="bg-white rounded-xl shadow-xl border border-[#e8dfd4] overflow-hidden" data-testid="service-hero-form-container">
              <div className="p-5 sm:p-6">
                <div className="text-center mb-4">
                  <h2 className="text-lg sm:text-xl font-bold text-[#4A331D]">Kostenlose Einschätzung anfordern</h2>
                  <p className="text-gray-500 text-sm mt-1">Wir melden uns schnellstmöglich</p>
                </div>
                <LeadFormWidget settings={settings} variant="hero" defaultService={data.formService} gewerkSlug={data.slug} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const LeistungenSection = ({ data }) => (
  <section className="py-16 sm:py-20 bg-white" data-testid="service-leistungen-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 sm:mb-14">
        <h2
          className="text-3xl sm:text-4xl font-bold text-[#4A331D] mb-3"
          style={{ fontFamily: "Work Sans, sans-serif" }}
        >
          Unsere Leistungen
        </h2>
        <p className="text-base sm:text-lg text-gray-600">{data.formService} – professionell umgesetzt</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
        {data.leistungen.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center gap-3 bg-[#faf8f5] border border-[#e8dfd4] p-5 rounded-xl hover:border-[#D4AA6A] hover:shadow-md transition-all duration-300"
            data-testid={`service-leistung-${idx}`}
          >
            <div className="w-10 h-10 bg-[#4A331D] rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-[#D4AA6A]" />
            </div>
            <span className="text-sm sm:text-base font-semibold text-[#4A331D]">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AblaufSection = ({ data }) => {
  if (!data.ablauf?.length) return null;
  return (
    <section className="py-16 sm:py-20 bg-[#4A331D]" data-testid="service-ablauf-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-3"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            So einfach geht&apos;s
          </h2>
          <p className="text-[#D4AA6A] text-base sm:text-lg">In vier Schritten zu Ihrem Projekt</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.ablauf.map((step, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center hover:bg-white/15 transition-colors"
              data-testid={`service-ablauf-step-${idx}`}
            >
              <div className="w-12 h-12 bg-[#D4AA6A] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#4A331D] font-bold text-lg">{idx + 1}</span>
              </div>
              <h3 className="text-lg font-bold text-white">{step}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqSection = ({ data }) => (
  <section className="py-16 sm:py-20 bg-[#faf8f5]" data-testid="service-faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2
          className="text-3xl sm:text-4xl font-bold text-[#4A331D] mb-3"
          style={{ fontFamily: "Work Sans, sans-serif" }}
        >
          Häufige Fragen
        </h2>
      </div>
      <div className="space-y-4">
        <Accordion type="single" collapsible className="space-y-3" data-testid="service-faq-accordion">
          {data.faq.map((item, idx) => (
            <AccordionItem
              key={idx}
              value={`faq-${idx}`}
              className="bg-white border border-[#e8dfd4] rounded-xl px-5 shadow-sm"
              data-testid={`service-faq-item-${idx}`}
            >
              <AccordionTrigger className="text-left text-base sm:text-lg font-bold text-[#4A331D] hover:no-underline py-4">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm sm:text-base text-gray-600 leading-relaxed pb-4">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

// Empfehlung (Doku, keine Code-Änderung nötig): Im Google-Ads-Konto Lead/Anruf/WhatsApp
// später als getrennte Conversion-Actions führen.
const ServiceLanding = ({ slug, settings, content }) => {
  const data = getServiceLandingBySlug(slug);
  useServiceSeo(data);

  if (!data) return null;

  return (
    <div className="min-h-screen bg-white" data-testid={`service-landing-${data.slug}`}>
      <Header />
      <main>
        <ServiceHero data={data} settings={settings} />
        <LeistungenSection data={data} />
        <AblaufSection data={data} />
        <Reviews content={content} />
        <Gallery />
        <ServiceAreas />
        <FaqSection data={data} />
        <FinalCTA settings={settings} content={content} defaultService={data.formService} gewerkSlug={data.slug} />
      </main>
      <Footer />
    </div>
  );
};

export default ServiceLanding;
